/**
 * Admin program-overview dashboard. Renders summary stats, the full founder
 * cohort table (data-room progress, plan status, last-active staleness,
 * engagement status), the EIR roster, and pending applications.
 *
 * Backed by the same getCEGOverview payload as CEGDashboard but adds export
 * actions: a program-wide report and per-founder progress downloads. Download
 * failures are intentionally silent — the browser simply won't trigger a save.
 */
import React, { useEffect, useState } from 'react';
import { getCEGOverview, downloadFounderExport, downloadProgramReport } from '../services/apiService';

const STAGE_LABELS = {
  idea: 'Idea', prototype: 'Prototype', pilot: 'Pilot', revenue: 'Revenue', scaling: 'Scaling',
};
const ENGAGEMENT_STYLES = {
  active:   null,
  dormant:  { background: '#fff8e1', color: '#7a5c00' },
  archived: { background: '#f1f5f9', color: '#475569' },
};
const STAGE_COLORS = {
  idea:      { background: '#f0e8ff', color: '#5b21b6' },
  prototype: { background: '#fef9c3', color: '#854d0e' },
  pilot:     { background: '#e0f2fe', color: '#0369a1' },
  revenue:   { background: '#dcfce7', color: '#15803d' },
  scaling:   { background: '#fff7ed', color: '#c2410c' },
};

// Whole days elapsed since dateStr (null if absent). Used to flag founders as
// stale when they haven't been active for 7+ days.
function daysSince(dateStr) {
  if (!dateStr) return null;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
}

function Stat({ label, value, accent }) {
  return (
    <div style={s.stat}>
      <p style={{ ...s.statValue, color: accent ? 'var(--color-rust)' : 'var(--color-forest)' }}>{value}</p>
      <p style={s.statLabel}>{label}</p>
    </div>
  );
}

/** Program-wide admin dashboard with cohort table and export actions. */
export default function AdminOverview() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(new Set());
  const [overallDownloading, setOverallDownloading] = useState(false);

  useEffect(() => {
    getCEGOverview().then(setOverview).finally(() => setLoading(false));
  }, []);

  async function handleOverallDownload() {
    setOverallDownloading(true);
    try {
      await downloadProgramReport();
    } catch (_) {
      // silent
    } finally {
      setOverallDownloading(false);
    }
  }

  async function handleDownload(founder) {
    setDownloading((prev) => new Set([...prev, founder.id]));
    try {
      await downloadFounderExport(founder.id, founder.company, founder.last_name);
    } catch (_) {
      // silent — browser will not trigger download if the request fails
    } finally {
      setDownloading((prev) => { const next = new Set(prev); next.delete(founder.id); return next; });
    }
  }

  if (loading) return <div style={s.loading}>Loading…</div>;
  if (!overview) return <div style={s.loading}>No data available.</div>;

  const { stats, founders, eirs, pending_applications } = overview;

  return (
    <div style={s.page} className="page-scroll">
      <div style={s.inner}>
        <div style={s.header}>
          <h1 style={s.title}>Program Overview</h1>
          <button
            style={{ ...s.actionBtn, opacity: overallDownloading ? 0.5 : 1 }}
            onClick={handleOverallDownload}
            disabled={overallDownloading}
          >
            {overallDownloading ? 'Generating…' : 'Download Venture Readiness Insights'}
          </button>
        </div>

        <div style={s.statsRow}>
          <Stat label="Active Founders"      value={stats.active_founders} />
          <Stat label="EIRs"                 value={stats.active_eirs} />
          <Stat label="Avg. Data Room"        value={`${stats.avg_data_room_pct}%`} />
          <Stat label="Pending Applications" value={stats.pending_applications} accent={stats.pending_applications > 0} />
        </div>

        <div style={s.card}>
          <h2 style={s.cardTitle}>Cohort</h2>
          <div style={s.tableScroll}>
          <table style={s.table}>
            <thead>
              <tr>
                {['Founder', 'Company', 'Stage', 'EIR', 'Data Room', 'Fundraising Plan', 'Last Active', 'Status', 'Actions'].map((h) => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {founders.map((f) => {
                const age = daysSince(f.last_active);
                const stale = age !== null && age >= 7;
                return (
                  <tr key={f.id} style={s.tr}>
                    <td style={s.td}><span style={s.founderName}>{f.first_name} {f.last_name}</span></td>
                    <td style={s.td}><span style={s.muted}>{f.company}</span></td>
                    <td style={s.td}>
                      <span style={{ ...s.stagePill, ...(STAGE_COLORS[f.stage] || {}) }}>
                        {STAGE_LABELS[f.stage] ?? f.stage ?? '—'}
                      </span>
                    </td>
                    <td style={s.td}><span style={s.muted}>{f.eir_name ?? '—'}</span></td>
                    <td style={s.td}>
                      <div style={s.progressWrap}>
                        <div style={s.progressTrack}>
                          <div style={{ ...s.progressFill, width: `${f.data_room_pct}%`, background: f.data_room_pct >= 80 ? '#15803d' : f.data_room_pct >= 50 ? 'var(--color-forest)' : '#c66b47' }} />
                        </div>
                        <span style={s.progressPct}>{f.data_room_pct}%</span>
                      </div>
                    </td>
                    <td style={s.td}>
                      <span style={{ ...s.badge, ...(f.has_fundraising ? s.badgeGreen : s.badgeGray) }}>
                        {f.has_fundraising ? 'Complete' : 'Not started'}
                      </span>
                    </td>
                    <td style={s.td}>
                      <span style={{ ...s.lastActive, color: stale ? '#c66b47' : 'var(--color-forest)' }}>
                        {age === null ? '—' : age === 0 ? 'Today' : age === 1 ? 'Yesterday' : `${age}d ago`}
                      </span>
                    </td>
                    <td style={s.td}>
               {ENGAGEMENT_STYLES[f.engagement_status] ? (
                        <span style={{ ...s.badge, ...ENGAGEMENT_STYLES[f.engagement_status] }}>
                          {f.engagement_status.charAt(0).toUpperCase() + f.engagement_status.slice(1)}
                        </span>
                      ) : (
                        <span style={{ ...s.badge, ...s.badgeGreen }}>Active</span>
                      )}
                    </td>
                    <td style={s.td}>
                      <button
                        style={{ ...s.actionBtn, opacity: downloading.has(f.id) ? 0.5 : 1 }}
                        onClick={() => handleDownload(f)}
                        disabled={downloading.has(f.id)}
                      >
                        {downloading.has(f.id) ? '…' : 'Download Progress'}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {founders.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ ...s.td, textAlign: 'center', opacity: 0.45 }}>No active founders yet.</td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>

        <div style={s.bottomRow}>
          <div style={{ ...s.card, flex: 2, marginBottom: 0 }}>
            <h2 style={s.cardTitle}>EIRs</h2>
            <div style={s.eirList}>
              {eirs.map((eir) => {
                const assigned = founders.filter((f) => f.eir_id === eir.id);
                return (
                  <div key={eir.id} style={s.eirCard}>
                    <div style={s.eirHeader}>
                      <div>
                        <p style={s.eirName}>{eir.first_name} {eir.last_name}</p>
                        <p style={s.eirEmail}>{eir.email}</p>
                      </div>
                      <a href={`mailto:${eir.email}`} style={s.emailBtn}>Email</a>
                    </div>
                    <div style={s.eirFounders}>
                      {assigned.map((f) => (
                        <span key={f.id} style={s.founderChip}>{f.first_name} {f.last_name}</span>
                      ))}
                      {assigned.length === 0 && <span style={{ ...s.muted, fontSize: 12 }}>No founders assigned</span>}
                    </div>
                  </div>
                );
              })}
              {eirs.length === 0 && <p style={{ ...s.muted, fontSize: 13 }}>No EIRs yet.</p>}
            </div>
          </div>

          {pending_applications.length > 0 && (
            <div style={{ ...s.card, flex: 1, marginBottom: 0 }}>
              <h2 style={s.cardTitle}>Pending Applications</h2>
              <div style={s.appList}>
                {pending_applications.map((a) => (
                  <div key={a.id} style={s.appRow}>
                    <div>
                      <p style={s.founderName}>{a.first_name} {a.last_name}</p>
                      <p style={s.eirEmail}>{a.email}</p>
                    </div>
                    <span style={s.muted}>
                      {a.applied_at ? new Date(a.applied_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  page:          { flex: 1, overflowY: 'auto', background: 'linear-gradient(to bottom, var(--color-frost) 0%, var(--color-mint-dark) 100%) fixed', padding: 'var(--space-8)' },
  inner:         { maxWidth: 1400, margin: '0 auto' },
  loading:       { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-body)', color: 'var(--color-forest)' },
  header:        { marginBottom: 'var(--space-6)', paddingTop: 'var(--space-4)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title:         { fontFamily: 'var(--font-accent)', fontSize: 36, fontWeight: 800, color: 'var(--color-rust)', lineHeight: 1.1 },
  statsRow:      { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-4)', marginBottom: 'var(--space-5)' },
  stat:          { background: 'var(--color-frost)', borderRadius: 'var(--radius-md)', padding: 'var(--space-5)', boxShadow: 'var(--shadow-card)', textAlign: 'center' },
  statValue:     { fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 800, marginBottom: 4 },
  statLabel:     { fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--color-forest)', opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.05em' },
  card:          { background: 'var(--color-frost)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)', boxShadow: 'var(--shadow-card)', marginBottom: 'var(--space-5)' },
  cardTitle:     { fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 800, color: 'var(--color-forest)', marginBottom: 'var(--space-4)' },
  tableScroll:   { overflowX: 'auto', marginRight: -4 },
  table:         { width: '100%', borderCollapse: 'collapse' },
  th:            { fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '6px 12px 10px', textAlign: 'left', borderBottom: '1.5px solid var(--color-mint-dark)', whiteSpace: 'nowrap' },
  tr:            { borderBottom: '1px solid var(--color-mint)' },
  td:            { padding: '10px 12px', verticalAlign: 'middle' },
  founderName:   { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--color-forest)' },
  muted:         { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.7 },
  stagePill:     { display: 'inline-block', padding: '2px 10px', borderRadius: 99, fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700 },
  progressWrap:  { display: 'flex', alignItems: 'center', gap: 8 },
  progressTrack: { flex: 1, height: 6, background: 'var(--color-mint-dark)', borderRadius: 99, overflow: 'hidden', minWidth: 60 },
  progressFill:  { height: '100%', borderRadius: 99 },
  progressPct:   { fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--color-forest)', minWidth: 30, textAlign: 'right' },
  badge:         { display: 'inline-block', padding: '3px 10px', borderRadius: 99, fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700 },
  badgeGreen:    { background: '#dcfce7', color: '#15803d' },
  badgeGray:     { background: '#f1f5f9', color: '#64748b' },
  lastActive:    { fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600 },
  actionBtn:     { padding: '5px 10px', borderRadius: 'var(--radius-sm)', background: 'var(--color-forest)', color: '#fff', fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' },
  bottomRow:     { display: 'flex', gap: 'var(--space-5)', alignItems: 'flex-start' },
  eirList:       { display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' },
  eirCard:       { background: 'var(--color-mint)', borderRadius: 'var(--radius-md)', padding: 'var(--space-4)' },
  eirHeader:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-3)' },
  eirName:       { fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 700, color: 'var(--color-forest)', marginBottom: 2 },
  eirEmail:      { fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--color-forest)', opacity: 0.6 },
  emailBtn:      { padding: '5px 14px', borderRadius: 'var(--radius-sm)', background: 'var(--color-forest)', color: '#fff', fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700, textDecoration: 'none', flexShrink: 0 },
  eirFounders:   { display: 'flex', flexWrap: 'wrap', gap: 6 },
  founderChip:   { padding: '3px 10px', borderRadius: 99, background: 'var(--color-frost)', fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--color-forest)', boxShadow: 'var(--shadow-card)' },
  appList:       { display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' },
  appRow:        { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--space-3)', background: 'var(--color-mint)', borderRadius: 'var(--radius-sm)' },
};
